import { useState } from "react";
import { Colors, Icon, type IconName } from "swing-components";

/********** Component **********/
export type DesktopNavigationProps = {
  menuItems: {
    top: Array<NavMenuItemProps>;
    bottom: Array<NavMenuItemProps>;
  };
  profile: ProfileMenuItemProps;
};

/**
 * Desktop navigation used in all page layouts.
 */
export function DesktopNavigation(props: DesktopNavigationProps) {
  const { menuItems, profile } = props;
  /***** Hooks *****/
  // Let's set initial state to whatever is saved in sessionStorage
  const [isMenuOpen, setIsMenuOpen] = useState(sessionStorage.getItem("isMenuExpanded") === "true");

  const handleOnExpand = () => {
    setIsMenuOpen(!isMenuOpen);
    sessionStorage.setItem("isMenuExpanded", String(!isMenuOpen));
  };

  /***** Render *****/
  return (
    <nav
      css={{
        width: !isMenuOpen ? 72 : 200,
        transition: "width 0.2s",
        height: "100vh",
        backgroundColor: Colors.blueStone200,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo Container */}
      <div
        css={{
          height: 112,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Logo */}
        <div css={{ height: 39, svg: { height: "inherit" } }}>
          {!isMenuOpen ? logomark : workmark}
        </div>

        {/* Expand Button */}
        <button
          data-testid="toggle-navigation-button"
          onClick={handleOnExpand}
          css={{
            position: "absolute",
            top: "50%",
            right: 0,
            padding: 4,
            // Forcing the height and width since on page load, the container can expand in a weird way.
            width: 36,
            height: 36,
            transform: "translateY(-50%) translateX(50%)",
            // TODO: @KoltonG - Variabalize this :P
            boxShadow: "0px 4px 8px rgba(24, 36, 56, 0.65)",
            borderRadius: "100%",
            backgroundColor: Colors.blueStone200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon name={!isMenuOpen ? "Chevron Right" : "Chevron Left"} color={Colors.slate200} />
        </button>
      </div>

      {/* Menu Item Container */}
      <div
        css={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: 24,
          // Add 16px gap between the top and bottom items in case they come
          // together.
          gap: 16,
        }}
      >
        {/* Top Items */}
        <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {menuItems.top?.map((menuItemProps) => (
            <NavMenuItem key={menuItemProps.label} {...menuItemProps} showLabel={isMenuOpen} />
          ))}
        </div>
        {/* Bottom Items */}
        <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {menuItems.bottom?.map((menuItemProps) => (
            <NavMenuItem key={menuItemProps.label} {...menuItemProps} showLabel={isMenuOpen} />
          ))}
        </div>
      </div>

      {/* Profile Image Container */}
      <div
        css={{
          padding: 16,
          backgroundColor: Colors.blueStone300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ProfileMenuItem {...profile} />
      </div>
    </nav>
  );
}

/********** Helper Components **********/
/** Shared props between the NavMenuItem and ProfileMenuItem components. */
type MenuItemProps = {
  /**
   * [Optional] When true, highlights the profile menu item.
   * @default false
   */
  isActive?: boolean;
  /** Handler when clicking on the menu item */
  onClick?: () => void;
};

type NavMenuItemProps = MenuItemProps & {
  iconName: IconName;
  label: string;
};

/** Renders a nav menu item with an icon and label which can also be highlighted. */
function NavMenuItem(props: NavMenuItemProps & { showLabel?: boolean }) {
  const { isActive = false, onClick, label, iconName, showLabel = false } = props;

  /***** Constants *****/
  const foregroundColor = !isActive ? Colors.slate200 : Colors.black;

  /***** Render *****/
  return (
    <button
      onClick={onClick}
      css={{
        width: "100%",
        padding: 8,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        gap: 8,
        color: foregroundColor,
        transition: "background-color 0.2s",
        backgroundColor: isActive ? Colors.yellow500 : "transparent",
        "&:hover": {
          // TODO: @KoltonG - Validate this style
          backgroundColor: Colors.blueStone300,
        },
      }}
    >
      {/* Icon */}
      <Icon name={iconName} color={foregroundColor} />
      {/* Label */}
      {showLabel && <MenuLabel label={label} />}
    </button>
  );
}

type ProfileMenuItemProps = MenuItemProps & {
  /**
   * [Optional] The name of the user.
   */
  name?: string;
  /**
   * @default an avatar icon
   */
  imageUrl?: string;
};

/** Renders the profile menu items which can handle an optional name and imageUrl. */
function ProfileMenuItem(props: ProfileMenuItemProps) {
  const { isActive, onClick, name, imageUrl } = props;

  /***** Constants *****/
  const foregroundColor = !isActive ? Colors.slate200 : Colors.black;

  /***** Render *****/
  return (
    <button
      onClick={onClick}
      css={{
        width: "100%",
        padding: 8,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        gap: 8,
        color: foregroundColor,
        cursor: onClick ? "pointer" : "default",
      }}
      className={`nav-menu-item ${isActive ? "active" : ""}`}
    >
      {/* Render the profile image or an avatar */}
      {imageUrl && (
        <img src={imageUrl} alt={name} css={{ width: 24, height: 24, borderRadius: "100%" }} />
      )}
      {!imageUrl && <Icon name="Person" color={foregroundColor} />}

      {/* Name */}
      {name && <MenuLabel label={name} />}
    </button>
  );
}

/** Render a label used within the MenuItem and ProfileItem component.*/
function MenuLabel(props: { label: string }) {
  const { label } = props;

  return (
    <span
      css={{
        fontSize: 14,
        lineHeight: "24px",
        margin: 0,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

/********** SVGs **********/
// FIXME: @KoltonG - once this moves into the `swing-components` we can move
// these assets to the `swing-assets` package. However, when doing so, we are
// losing the SVG benefits as its imported as an image. Let's resolve this issue
// later.
const logomark = (
  <svg width="32" height="39" viewBox="0 0 32 39" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.3752 0.56626C18.7155 0.443708 19.3824 0.25307 19.8724 0.157751C20.4441 0.0215814 21.3424 -0.0192695 22.4586 0.00796443C23.7925 0.0488153 24.3777 0.130517 25.1808 0.38924C25.7389 0.56626 26.6372 0.947536 27.1544 1.24711C27.7261 1.5603 28.5156 2.17306 29.1417 2.79944C29.7134 3.37136 30.4212 4.25646 30.7342 4.78752C31.0473 5.30497 31.3059 5.78156 31.3059 5.83603C31.3059 5.90411 30.462 6.43518 29.4411 7.04794C27.7806 8.02836 27.5492 8.12368 27.4267 7.91943C27.345 7.79687 27.0456 7.30666 26.7325 6.84369C26.433 6.39433 25.8886 5.78156 25.5075 5.4956C25.14 5.22327 24.5139 4.86922 24.1327 4.71944C23.738 4.56965 23.0847 4.40625 22.7036 4.3654C22.3088 4.31093 21.5466 4.35178 21.0294 4.43348C20.4713 4.5288 19.7771 4.76029 19.3688 4.99178C18.8879 5.26412 18.534 5.59092 18.3071 5.9722C18.1166 6.29901 17.926 6.8573 17.8716 7.23858C17.8171 7.71517 17.8443 8.09645 18.0077 8.53219C18.1302 8.87261 18.4432 9.34921 18.7018 9.5807C18.9741 9.8258 19.4777 10.1798 19.8452 10.3569C20.1991 10.5339 21.043 10.8879 21.7099 11.1603C22.3769 11.419 24.0647 12.0454 25.453 12.5492C26.8414 13.0394 28.2433 13.6113 28.5836 13.802C28.9239 14.0062 29.5909 14.5373 30.0673 14.9866C30.53 15.4496 31.0881 16.0896 31.2787 16.43C31.4692 16.7705 31.7142 17.3151 31.8231 17.6556C31.932 18.0096 32.0001 18.8811 32.0001 19.7662C32.0001 20.6921 31.9184 21.5773 31.7823 22.0811C31.6598 22.5304 31.3603 23.2658 31.1289 23.7015C30.8839 24.1372 30.3259 24.8453 29.8767 25.2675C29.4139 25.7032 28.6109 26.2479 28.0392 26.5338C27.4811 26.8062 26.5283 27.1738 25.9294 27.3508C24.9903 27.6232 24.5411 27.664 22.5266 27.664C20.5258 27.664 20.063 27.6232 19.1238 27.3508C18.5249 27.1738 17.4768 26.7653 16.8099 26.4385C16.1293 26.1117 15.2582 25.5534 14.8499 25.1857C14.4415 24.8317 13.8562 24.1645 13.5296 23.7151C13.2165 23.2658 12.7809 22.5304 12.5904 22.0811C12.3862 21.6317 12.2501 21.2096 12.2909 21.1279C12.3182 21.0462 13.1348 20.5287 14.1012 19.9704C15.054 19.4122 15.8979 18.9356 15.9524 18.9083C16.0068 18.8947 16.2246 19.2079 16.4152 19.5892C16.6193 19.9841 17.0004 20.6104 17.2727 20.9781C17.5449 21.3458 18.0757 21.8632 18.4432 22.1355C18.8243 22.3943 19.5185 22.7619 20.0085 22.9253C20.4985 23.1024 21.4377 23.2658 22.1183 23.3202C23.0166 23.3883 23.6155 23.3611 24.3641 23.1977C25.0039 23.0615 25.63 22.8164 26.0655 22.5304C26.5193 22.2309 26.8686 21.8541 27.1136 21.4002C27.3994 20.8964 27.4947 20.5287 27.4947 19.9704C27.4947 19.5347 27.3858 18.99 27.2361 18.6768C27.1136 18.3773 26.8142 17.9824 26.5964 17.7917C26.3786 17.6011 25.4666 17.1381 24.5683 16.7568C23.67 16.3756 22.023 15.7356 20.8933 15.3543C19.7771 14.9594 18.2935 14.3739 17.6266 14.0471C16.9596 13.7339 16.0885 13.23 15.6938 12.9441C15.3126 12.6445 14.7818 12.1407 14.5096 11.8003C14.251 11.4598 13.8971 10.8198 13.7337 10.3705C13.5432 9.83942 13.4207 9.09049 13.3662 8.25985C13.3254 7.34751 13.3662 6.66667 13.516 5.94497C13.6385 5.38667 13.9243 4.55603 14.1421 4.10667C14.3598 3.65731 14.9043 2.922 15.3399 2.47264C15.7754 2.02328 16.4968 1.46498 16.946 1.21988C17.3952 0.988387 18.0349 0.688813 18.3752 0.56626Z"
      fill="white"
    />
    <path
      d="M0.966401 13.7203C1.22501 13.5296 1.57891 13.339 1.76947 13.2845C1.96002 13.2301 2.27308 13.1756 2.49086 13.1756C2.69503 13.162 3.08976 13.2573 3.3756 13.3798C3.64782 13.4888 4.02894 13.7203 4.19227 13.8837C4.36922 14.0471 4.60061 14.333 4.69589 14.5237C4.79117 14.7143 4.91367 15.4769 4.96812 16.2258C5.02256 16.9747 5.15867 18.1458 5.26756 18.813C5.39007 19.4802 5.66229 20.5968 5.88007 21.2641C6.09785 21.9313 6.50619 22.9798 6.80564 23.579C7.09147 24.1781 7.63593 25.1313 8.00343 25.6896C8.38455 26.2479 9.03789 27.1194 9.48706 27.6096C9.92262 28.0998 10.6712 28.8623 11.1612 29.2981C11.6513 29.7202 12.5088 30.3874 13.0668 30.7687C13.6249 31.15 14.7002 31.7491 15.4488 32.1032C16.1974 32.4572 17.368 32.9202 18.0349 33.1108C18.7019 33.3015 19.8452 33.5602 20.553 33.6555C21.2608 33.7645 22.2408 33.8598 22.7308 33.8598C23.3025 33.8598 23.8061 33.9551 24.16 34.1185C24.4595 34.2547 24.8406 34.5134 25.0039 34.6904C25.1672 34.8674 25.3578 35.1806 25.4395 35.3849C25.5211 35.6027 25.5892 36.0385 25.5892 36.3789C25.5892 36.7193 25.5075 37.1959 25.4123 37.4274C25.3034 37.6725 25.0311 38.0402 24.7997 38.2444C24.5547 38.4623 24.1056 38.6938 23.7925 38.7755C23.3569 38.8844 22.7308 38.8844 21.3697 38.7755C20.3625 38.6802 18.9741 38.4895 18.3072 38.3398C17.6402 38.2036 16.4969 37.8904 15.7891 37.6589C15.0813 37.4274 13.8835 36.9372 13.1349 36.5832C12.3863 36.2291 11.134 35.5074 10.3446 34.9764C9.55511 34.4589 8.39816 33.5738 7.75843 33.0155C7.1187 32.4572 6.16591 31.4768 5.63507 30.8504C5.10423 30.224 4.31477 29.1891 3.87921 28.5491C3.44365 27.9092 2.80392 26.8062 2.45003 26.0981C2.09614 25.39 1.60613 24.2462 1.37474 23.579C1.12974 22.9117 0.816676 21.8632 0.666952 21.2641C0.503617 20.6649 0.299448 19.5347 0.190558 18.7449C0.0952789 17.9551 0 16.8249 0 16.2258C0 15.4769 0.0816676 14.973 0.245003 14.5917C0.381116 14.2922 0.707786 13.8973 0.966401 13.7203Z"
      fill="#FFD100"
    />
  </svg>
);

const workmark = (
  <svg width="200" height="58" viewBox="0 0 200 58" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.13402 20.5755C1.33652 20.3727 1.88328 20.0888 2.32878 19.9265C2.77429 19.744 3.3818 19.6223 3.68555 19.6021C4.00955 19.6021 4.59681 19.744 5.00182 19.9265C5.42707 20.0888 5.99408 20.4335 6.23708 20.6769C6.50034 20.9202 6.84459 21.3461 6.98635 21.63C7.1281 21.9139 7.31035 23.009 7.39135 24.0636C7.4521 25.1181 7.6546 26.8622 7.83686 27.9167C7.99886 28.9713 8.40386 30.6139 8.70762 31.5671C9.01137 32.5202 9.63913 34.0615 10.0846 35.0146C10.5301 35.9678 11.4009 37.4684 11.9882 38.3608C12.5754 39.2531 13.9322 40.8957 15.0055 42.0111C16.0787 43.1265 17.7595 44.6272 18.7518 45.3167C19.744 46.0265 21.5665 47.1013 22.7816 47.7097C23.9966 48.2978 25.8394 49.0279 26.8316 49.3118C27.8239 49.5957 29.5249 49.981 30.5779 50.123C31.6309 50.2852 33.0485 50.4272 33.7167 50.4272C34.385 50.4272 35.2557 50.5083 35.6405 50.6097C36.0455 50.7314 36.6732 51.1572 37.0985 51.6237C37.5238 52.0901 37.9288 52.6985 37.9895 53.023C38.0705 53.3272 38.0908 54.0369 38.0503 54.6048C37.9895 55.3146 37.8073 55.8418 37.4023 56.3691C37.0985 56.7747 36.5518 57.2817 36.2075 57.4845C35.6607 57.7684 35.0937 57.8292 33.2105 57.7887C31.9347 57.7684 29.7882 57.5453 28.4516 57.3223C27.1151 57.0992 24.9281 56.5313 23.5916 56.0852C22.2551 55.639 20.3515 54.8684 19.339 54.3817C18.3265 53.895 16.7065 52.9621 15.694 52.3132C14.6814 51.6439 13.0614 50.4272 12.0489 49.5754C11.0364 48.7439 9.49738 47.223 8.60637 46.1887C7.71535 45.1544 6.45984 43.5321 5.81183 42.5586C5.14357 41.5852 4.09056 39.6992 3.44255 38.3608C2.79454 37.0223 1.98453 35.0552 1.66052 34.0006C1.31627 32.9461 0.911262 31.3845 0.72901 30.5531C0.567008 29.7216 0.324004 28.2615 0.222753 27.3083C0.101251 26.3552 0 24.895 0 24.0636C0 23.0293 0.121502 22.279 0.384755 21.7314C0.587258 21.2853 0.931513 20.758 1.13402 20.5755Z"
      fill="#FFD100"
    />
    <path
      d="M27.3379 0.843328C27.8442 0.660811 28.8365 0.376896 29.5655 0.234938C30.416 0.0321411 31.7525 -0.028698 33.413 0.0118614C35.3976 0.0727004 36.2683 0.194378 37.4631 0.579692C38.2933 0.843328 39.6299 1.41116 40.3994 1.85731C41.2499 2.32374 42.4244 3.23633 43.3559 4.1692C44.2064 5.02094 45.2594 6.3594 45.7252 7.13003C46.1909 7.92094 46.5757 8.61045 46.5757 8.71184C46.5757 8.79296 45.3202 9.58387 43.7812 10.4965C41.3511 11.9363 40.9866 12.0985 40.8044 11.7944C40.6829 11.6118 40.2374 10.902 39.7919 10.2125C39.3464 9.54331 38.5768 8.651 38.0706 8.24541C37.5643 7.83982 36.6328 7.31255 35.9848 7.06919C35.357 6.82583 34.304 6.5622 33.656 6.50136C33.0283 6.42024 31.9145 6.48108 31.1855 6.60276C30.4565 6.74472 29.3832 7.10975 28.796 7.43422C28.1075 7.83982 27.5607 8.32653 27.2367 8.89436C26.9127 9.46219 26.6899 10.2125 26.6089 10.9832C26.5077 11.9972 26.5684 12.3419 26.9734 13.1125C27.2367 13.6195 27.8037 14.2888 28.2492 14.6335C28.6947 14.9783 29.8895 15.6069 30.8817 16.0125C31.874 16.4384 34.709 17.5132 37.1593 18.4055C39.6096 19.2978 42.0194 20.2713 42.5256 20.5552C43.0319 20.8594 44.0647 21.6908 44.8139 22.4209C45.7049 23.2727 46.4137 24.1852 46.7985 24.9761C47.1832 25.7671 47.4465 26.7202 47.568 27.7139C47.6692 28.5859 47.6692 30.0055 47.568 31.0195C47.4667 31.9929 47.2035 33.2705 46.9807 33.8586C46.7782 34.4265 46.2922 35.3593 45.9277 35.9069C45.5632 36.4544 44.7532 37.3467 44.1457 37.8943C43.5382 38.4216 42.4851 39.1314 41.8169 39.4558C41.1486 39.7803 40.0146 40.2467 39.2856 40.4698C38.5566 40.7132 37.3821 40.9971 36.6531 41.139C35.8836 41.2607 34.223 41.3013 32.603 41.2404C30.659 41.1796 29.4237 41.0174 28.3505 40.6929C27.5202 40.4495 26.1837 39.9628 25.4142 39.5978C24.6447 39.2328 23.4296 38.5027 22.7209 37.9754C22.0324 37.4684 21.0604 36.5153 20.5541 35.8866C20.0478 35.2377 19.2986 34.0614 18.8936 33.2908C18.5088 32.5202 18.2253 31.7293 18.2658 31.567C18.3266 31.4048 19.5618 30.5531 21.0401 29.7013C22.4981 28.8293 23.7334 28.1195 23.7941 28.1195C23.8549 28.1195 24.1384 28.6062 24.4219 29.174C24.7054 29.7621 25.2927 30.7153 25.6977 31.2628C26.1229 31.8307 26.9532 32.6013 27.5607 33.0069C28.1682 33.4328 29.2415 33.96 29.9705 34.2237C31.064 34.5887 31.712 34.6698 33.7168 34.6901C35.5393 34.6901 36.4101 34.5887 37.1593 34.3251C37.7061 34.1223 38.5161 33.7167 38.9616 33.4328C39.3869 33.1286 40.0146 32.4391 40.3184 31.8712C40.7639 31.1209 40.9056 30.5936 40.9056 29.8027C40.9056 29.2146 40.7639 28.3831 40.5816 27.9775C40.4196 27.5517 40.0754 26.9838 39.8324 26.7405C39.5894 26.4768 38.9818 26.051 38.4756 25.7873C37.9693 25.5237 36.6126 24.9356 35.4381 24.4692C34.2635 24.0027 32.2588 23.2524 30.983 22.8062C29.7072 22.3601 28.0669 21.7517 27.3379 21.4475C26.6089 21.1433 25.3737 20.5146 24.6042 20.0685C23.8346 19.602 22.7816 18.8314 22.2754 18.3244C21.7691 17.8174 21.1211 16.9251 20.8376 16.3167C20.5338 15.7286 20.1896 14.7552 20.0681 14.1671C19.9466 13.5992 19.8453 12.4027 19.8453 11.551C19.8453 10.6181 20.0073 9.29995 20.2503 8.34681C20.4933 7.4545 20.9186 6.27828 21.2224 5.71045C21.5261 5.1629 22.2754 4.20975 22.8829 3.58108C23.4904 2.97269 24.5434 2.18179 25.2117 1.81675C25.8799 1.472 26.8317 1.02585 27.3379 0.843328Z"
      fill="white"
    />
    <path
      d="M173.241 0.965006C174.031 0.74193 175.388 0.397175 176.279 0.234938C177.372 0.0321411 178.851 -0.028698 180.835 0.0118614C183.022 0.0524207 184.217 0.194378 185.493 0.518853C186.444 0.76221 188.226 1.45172 189.441 2.06011C190.697 2.68878 192.378 3.72304 193.289 4.45311C194.241 5.20346 195.435 6.44052 196.185 7.39366C196.873 8.30625 197.44 9.17828 197.44 9.32023C197.44 9.44191 196.245 10.2531 194.767 11.0846C193.309 11.916 192.054 12.6258 191.993 12.6664C191.932 12.7069 191.385 12.1391 190.818 11.409C190.231 10.679 189.259 9.70555 188.692 9.27967C188.105 8.83352 187.032 8.18457 186.303 7.81954C185.574 7.47478 184.116 6.98807 183.063 6.78527C181.605 6.48108 180.693 6.39996 179.316 6.48108C178.304 6.54192 176.947 6.74471 176.279 6.92723C175.61 7.08947 174.436 7.5559 173.646 7.94122C172.856 8.32653 171.662 9.09716 170.973 9.66499C170.264 10.2125 169.292 11.2468 168.806 11.9566C168.3 12.6461 167.632 13.802 167.288 14.5321C166.943 15.2622 166.518 16.4992 166.356 17.2699C166.194 18.0405 166.052 19.5615 166.052 20.616C166.052 21.8531 166.194 23.151 166.457 24.2258C166.68 25.1384 167.126 26.416 167.45 27.065C167.774 27.6936 168.442 28.7684 168.948 29.4377C169.434 30.1069 170.386 31.0803 171.034 31.5873C171.702 32.0943 172.553 32.6824 172.937 32.9055C173.322 33.1489 174.274 33.5545 175.064 33.8586C175.853 34.1426 177.291 34.4873 178.304 34.609C179.337 34.751 180.835 34.8118 181.746 34.7307C182.637 34.6698 183.913 34.467 184.581 34.3048C185.25 34.1223 186.485 33.6558 187.315 33.2705C188.328 32.7838 189.259 32.1349 190.11 31.2831C190.879 30.5531 191.649 29.5188 192.054 28.7279C192.438 27.9978 192.843 27.065 193.188 25.8887H180.025V19.8048H199.87L199.972 20.981C200.032 21.6097 199.992 23.1915 199.891 24.4692C199.769 25.7468 199.486 27.4908 199.263 28.3223C199.02 29.1538 198.514 30.4922 198.109 31.2628C197.724 32.0335 197.116 33.088 196.772 33.595C196.428 34.102 195.557 35.116 194.828 35.8461C194.099 36.5761 192.803 37.6307 191.973 38.1579C191.142 38.7055 189.948 39.3747 189.34 39.6384C188.733 39.902 187.356 40.3481 186.303 40.6321C184.946 40.9768 183.569 41.1593 181.645 41.2404C179.661 41.3216 178.344 41.2607 176.785 41.0174C175.61 40.8349 173.747 40.3684 172.634 39.9831C171.52 39.5978 169.9 38.8474 169.029 38.3202C168.138 37.7726 166.741 36.6978 165.89 35.9069C165.02 35.116 163.865 33.8384 163.278 33.0677C162.711 32.2971 161.82 30.7964 161.314 29.7419C160.807 28.6873 160.2 27.0852 159.977 26.1929C159.754 25.3006 159.491 23.658 159.37 22.5426C159.268 21.2852 159.268 19.744 159.37 18.4866C159.471 17.3713 159.795 15.6475 160.078 14.6335C160.382 13.6398 160.969 12.0783 161.415 11.186C161.881 10.2937 162.63 9.01604 163.116 8.34681C163.582 7.67758 164.696 6.44052 165.566 5.60905C166.457 4.77759 167.672 3.7636 168.28 3.35801C168.887 2.95241 169.94 2.34402 170.609 1.99927C171.277 1.6748 172.451 1.20836 173.241 0.965006Z"
      fill="white"
    />
    <path
      d="M54.7771 20.616C51.7193 9.80695 49.2082 0.904167 49.2082 0.843328C49.2082 0.782489 50.808 0.74193 52.752 0.74193C55.9516 0.74193 56.2958 0.782489 56.3768 1.08668C56.4376 1.28948 58.2601 8.10345 60.4471 16.2559C62.6342 24.4083 64.4364 31.0803 64.4567 31.1006C64.4769 31.1209 66.4412 24.4692 68.8308 16.2964C71.2203 8.12373 73.1846 1.28948 73.2251 1.08668C73.3061 0.782489 73.6098 0.74193 79.5837 0.74193L80.0494 2.30347C80.3127 3.17549 82.2364 9.88807 84.3627 17.209C86.4688 24.5503 88.2305 30.5531 88.2913 30.5531C88.352 30.5531 88.919 28.809 89.5468 26.6594C90.1745 24.5097 91.9768 17.9594 93.5563 12.0985C95.1359 6.23772 96.4521 1.28948 96.5129 1.10696C96.5939 0.782489 96.8976 0.74193 100.097 0.802769C103.459 0.843328 103.58 0.863608 103.519 1.24892C103.479 1.472 101.029 10.2328 98.0722 20.7174C95.1156 31.202 92.6248 39.9426 92.5236 40.1453C92.3616 40.4495 91.9566 40.4901 84.7475 40.4901L80.7177 26.558C78.5104 18.872 76.6474 12.5041 76.5664 12.3622C76.5056 12.2202 75.2703 16.1545 73.8326 21.0824L73.726 21.4477C72.3031 26.3248 70.4732 32.597 68.1422 40.4901H64.2542C62.1077 40.4901 60.3459 40.4495 60.3459 40.3887C60.3459 40.3279 57.8349 31.4454 54.7771 20.616Z"
      fill="white"
    />
    <path
      d="M108.542 14.6335C108.542 10.4965 108.602 5.69017 108.683 3.94612L108.805 0.74193H115.305L115.366 6.3594C115.406 9.46219 115.427 17.4321 115.427 24.0636C115.427 30.695 115.366 37.1034 115.123 40.4901H108.845L108.683 31.3237C108.602 26.274 108.542 18.7503 108.542 14.6335Z"
      fill="white"
    />
    <path
      d="M123.223 40.1859C123.182 40.0237 123.142 31.0803 123.142 20.3118V0.74193L128.488 0.843328L135.474 10.9223C139.322 16.4587 143.433 22.2992 146.713 26.8013L146.774 13.9237C146.815 4.04752 146.896 1.02585 147.078 0.904167C147.22 0.823049 148.718 0.762209 153.497 0.74193V40.4901L148.151 40.3887L129.906 14.1265L129.804 40.4901H126.564C123.871 40.4901 123.324 40.4495 123.223 40.1859Z"
      fill="white"
    />
  </svg>
);
